import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import KofiModal from "../components/KofiModal";
import { 
  ArrowLeft, 
  Sword, 
  Calendar as CalendarIcon, 
  TrendingUp, 
  Trophy,
  Target,
  Clock,
  Heart,
  Flame,
  BarChart3,
  Star,
  Filter,
  X
} from "lucide-react";
import { format } from "date-fns";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Boss image mapping
const BOSS_IMAGE_MAPPING = {
  "Mano": "https://legends.ml/static/images/lib/monster/2220000.png",
  "Stumpy": "https://legends.ml/static/images/lib/monster/3220000.png",
  "Deo": "https://legends.ml/static/images/lib/monster/3220001.png",
  "King Clang": "https://legends.ml/static/images/lib/monster/5220000.png",
  "Seruf": "https://legends.ml/static/images/lib/monster/4220001.png",
  "Faust": "https://legends.ml/static/images/lib/monster/5220002.png",
  "Giant Centipede": "https://legends.ml/static/images/lib/monster/9500177.png",
  "Timer": "https://legends.ml/static/images/lib/monster/5220003.png",
  "Mushmom": "https://legends.ml/static/images/lib/monster/6130101.png",
  "Dyle": "https://legends.ml/static/images/lib/monster/6220000.png",
  "Zombie Mushmom": "https://legends.ml/static/images/lib/monster/6300005.png",
  "Zeno": "https://legends.ml/static/images/lib/monster/6220001.png",
  "Nine-Tailed Fox": "https://legends.ml/static/images/lib/monster/7220001.png",
  "Tae Roon": "https://legends.ml/static/images/lib/monster/7220000.png",
  "King Sage Cat": "https://legends.ml/static/images/lib/monster/7220002.png",
  "Jr. Balrog": "https://legends.ml/static/images/lib/monster/8130100.png",
  "Eliza": "https://legends.ml/static/images/lib/monster/8220000.png",
  "Snack Bar": "https://legends.ml/static/images/lib/monster/9410015.png",
  "Chimera": "https://legends.ml/static/images/lib/monster/8220002.png",
  "Blue Mushmom": "https://legends.ml/static/images/lib/monster/9400205.png",
  "Snowman": "https://legends.ml/static/images/lib/monster/8220001.png",
  "Headless Horseman": "https://legends.ml/static/images/lib/monster/9400549.png",
  "Manon": "https://legends.ml/static/images/lib/monster/9500174.png",
  "Griffey": "https://legends.ml/static/images/lib/monster/8180001.png",
  "Pianus (16h)": "https://legends.ml/static/images/lib/monster/8510000.png",
  "Pianus (24h)": "https://legends.ml/static/images/lib/monster/8520000.png",
  "BigFoot": "https://legends.ml/static/images/lib/monster/9400575.png",
  "Black Crow": "https://legends.ml/static/images/lib/monster/9400014.png",
  "Leviathan": "https://legends.ml/static/images/lib/monster/8220003.png",
  "Kacchuu Musha": "https://legends.ml/static/images/lib/monster/9400405.png",
  "Dodo": "https://legends.ml/static/images/lib/monster/8220004.png",
  "Anego / Female Boss": "https://legends.ml/static/images/lib/monster/9400121.png",
  "Lilynouch": "https://legends.ml/static/images/lib/monster/8220005.png",
  "Lyka": "https://legends.ml/static/images/lib/monster/8220006.png"
};

const getBossImage = (bossName) => {
  // Extract base boss name (remove location suffix)
  const baseName = bossName.split(" - ")[0];
  return BOSS_IMAGE_MAPPING[baseName] || "https://legends.ml/static/images/lib/monster/6130101.png";
};

const PERIOD_OPTIONS = [
  { value: "all", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "custom", label: "Custom Range" }
];

const StatsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [filteredStats, setFilteredStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showKofiModal, setShowKofiModal] = useState(false);
  
  // Filter state
  const [selectedPeriod, setSelectedPeriod] = useState("all");
  const [customDateFrom, setCustomDateFrom] = useState(null);
  const [customDateTo, setCustomDateTo] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [loadingFiltered, setLoadingFiltered] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }

    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/stats/kills`);
        setStats(response.data);
        setFilteredStats(null);
      } catch (error) {
        toast.error("Failed to load statistics");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [user, navigate]);

  const fetchFilteredStats = async (period, dateFrom = null, dateTo = null) => {
    if (period === "all") {
      setFilteredStats(null);
      return;
    }

    setLoadingFiltered(true);
    try {
      let url = `${API_URL}/api/stats/kills/filtered`;
      const params = new URLSearchParams();
      
      if (period === "custom" && dateFrom && dateTo) {
        params.append("date_from", dateFrom.toISOString());
        params.append("date_to", dateTo.toISOString());
      } else if (period !== "custom") {
        params.append("period", period);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await axios.get(url);
      setFilteredStats(response.data);
    } catch (error) {
      toast.error("Failed to load filtered statistics");
      console.error(error);
    } finally {
      setLoadingFiltered(false);
    }
  };

  const handlePeriodChange = (period) => {
    setSelectedPeriod(period);
    if (period === "custom") {
      setShowDatePicker(true);
    } else {
      setShowDatePicker(false);
      fetchFilteredStats(period);
    }
  };

  const applyCustomDateRange = () => {
    if (customDateFrom && customDateTo) {
      fetchFilteredStats("custom", customDateFrom, customDateTo);
      setShowDatePicker(false);
    } else {
      toast.error("Please select both start and end dates");
    }
  };

  const clearFilter = () => {
    setSelectedPeriod("all");
    setFilteredStats(null);
    setCustomDateFrom(null);
    setCustomDateTo(null);
    setShowDatePicker(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <p className="text-muted-foreground">No statistics available</p>
          <Button onClick={() => navigate("/")} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Tracker
          </Button>
        </div>
      </div>
    );
  }

  // Use filtered stats if available, otherwise use all stats
  const displayStats = filteredStats || stats;
  const isFiltered = filteredStats !== null;

  // Get top 10 bosses by kills
  const topBosses = Object.entries(isFiltered ? (filteredStats.kills_by_boss || {}) : (stats.kills_by_boss || {}))
    .sort(([, a], [, b]) => b - a)
    .slice(0, 10);

  const recentKills = isFiltered ? (filteredStats.recent_kills || []) : (stats.recent_kills || []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="stats-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">My Kill Statistics</h1>
              <p className="text-sm text-muted-foreground">{user?.username}'s hunting record</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Date Filter Section */}
        <Card className="bg-card/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Filter className="w-4 h-4" />
              Filter by Period
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-4">
              {PERIOD_OPTIONS.map((option) => (
                <Button
                  key={option.value}
                  size="sm"
                  variant={selectedPeriod === option.value ? "default" : "outline"}
                  onClick={() => handlePeriodChange(option.value)}
                  disabled={loadingFiltered}
                >
                  {option.label}
                </Button>
              ))}
              {isFiltered && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={clearFilter}
                  className="text-muted-foreground"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>

            {/* Custom Date Range Picker */}
            {showDatePicker && (
              <div className="flex flex-wrap items-end gap-4 p-4 rounded-lg bg-background/50 border border-border/50">
                <div className="space-y-2">
                  <Label>From</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customDateFrom ? format(customDateFrom, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customDateFrom}
                        onSelect={setCustomDateFrom}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>To</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-[200px] justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {customDateTo ? format(customDateTo, "PPP") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={customDateTo}
                        onSelect={setCustomDateTo}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <Button onClick={applyCustomDateRange} disabled={loadingFiltered}>
                  Apply
                </Button>
              </div>
            )}

            {/* Active Filter Indicator */}
            {isFiltered && (
              <div className="mt-3 p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-primary">
                  Showing stats for: <span className="font-semibold">
                    {selectedPeriod === "custom" && customDateFrom && customDateTo
                      ? `${format(customDateFrom, "MMM d, yyyy")} - ${format(customDateTo, "MMM d, yyyy")}`
                      : PERIOD_OPTIONS.find(o => o.value === selectedPeriod)?.label
                    }
                  </span>
                  {" "}({filteredStats.filtered_kills} kills)
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {isFiltered ? filteredStats.filtered_kills : stats.total_kills}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isFiltered ? "Period Kills" : "Total Kills"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.weekly_kills}</p>
                  <p className="text-xs text-muted-foreground">This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="w-5 h-5 text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.monthly_kills}</p>
                  <p className="text-xs text-muted-foreground">This Month</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.yearly_kills}</p>
                  <p className="text-xs text-muted-foreground">This Year</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enhanced Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                  <Flame className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.daily_kills || 0}</p>
                  <p className="text-xs text-muted-foreground">Today</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.daily_average || 0}</p>
                  <p className="text-xs text-muted-foreground">Daily Avg</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-rose-500/20 rounded-lg flex items-center justify-center">
                  <Star className="w-5 h-5 text-rose-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {(isFiltered && filteredStats.best_day?.kills) || stats.best_day?.kills || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {isFiltered ? "Period Best" : "Best Day"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-500/20 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-orange-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.weekly_average || 0}</p>
                  <p className="text-xs text-muted-foreground">Weekly Avg</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Kills by Day of Week + Weekly Trend */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Kills by Day of Week */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CalendarIcon className="w-5 h-5" />
                Activity by Day
                {isFiltered && <span className="text-xs font-normal text-muted-foreground">(filtered)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(() => {
                const dayData = isFiltered ? filteredStats.kills_by_day : stats.kills_by_day;
                return dayData && Object.keys(dayData).length > 0 ? (
                  <div className="space-y-3">
                    {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => {
                      const count = dayData[day] || 0;
                      const maxKills = Math.max(...Object.values(dayData), 1);
                      const percentage = (count / maxKills) * 100;
                      return (
                        <div key={day} className="flex items-center gap-3">
                          <span className="w-10 text-sm text-muted-foreground">{day}</span>
                          <div className="flex-1 h-6 bg-background/50 rounded overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-primary/60 to-primary rounded transition-all duration-500"
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <span className="w-10 text-sm font-medium text-right">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-muted-foreground text-center py-8">
                    No activity data {isFiltered ? "for this period" : "yet"}
                  </p>
                );
              })()}
            </CardContent>
          </Card>

          {/* Weekly Trend */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Weekly Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stats.kills_by_week && stats.kills_by_week.length > 0 ? (
                <div className="space-y-3">
                  {stats.kills_by_week.map((weekData, index) => {
                    const maxKills = Math.max(...stats.kills_by_week.map(w => w.kills), 1);
                    const percentage = (weekData.kills / maxKills) * 100;
                    const isCurrentWeek = index === stats.kills_by_week.length - 1;
                    return (
                      <div key={weekData.week} className="flex items-center gap-3">
                        <span className={`w-24 text-xs truncate ${isCurrentWeek ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                          {weekData.week}
                        </span>
                        <div className="flex-1 h-6 bg-background/50 rounded overflow-hidden">
                          <div 
                            className={`h-full rounded transition-all duration-500 ${
                              isCurrentWeek 
                                ? 'bg-gradient-to-r from-emerald-500/60 to-emerald-400' 
                                : 'bg-gradient-to-r from-blue-500/40 to-blue-400/60'
                            }`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className={`w-10 text-sm font-medium text-right ${isCurrentWeek ? 'text-emerald-400' : ''}`}>
                          {weekData.kills}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-8">
                  No weekly data yet
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Monthly Trend */}
        {stats.kills_by_month && stats.kills_by_month.length > 0 && (
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Monthly Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-2 h-40">
                {stats.kills_by_month.map((monthData, index) => {
                  const maxKills = Math.max(...stats.kills_by_month.map(m => m.kills), 1);
                  const heightPercentage = (monthData.kills / maxKills) * 100;
                  const isCurrentMonth = index === stats.kills_by_month.length - 1;
                  return (
                    <div key={monthData.month} className="flex flex-col items-center flex-1">
                      <span className={`text-xs mb-1 ${isCurrentMonth ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                        {monthData.kills}
                      </span>
                      <div 
                        className={`w-full rounded-t transition-all duration-500 ${
                          isCurrentMonth 
                            ? 'bg-gradient-to-t from-primary to-primary/60' 
                            : 'bg-gradient-to-t from-muted to-muted/60'
                        }`}
                        style={{ height: `${Math.max(heightPercentage, 5)}%` }}
                      />
                      <span className={`text-xs mt-2 ${isCurrentMonth ? 'text-primary font-medium' : 'text-muted-foreground'}`}>
                        {monthData.month}
                      </span>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Best Day Highlight */}
        {((isFiltered && filteredStats.best_day) || (!isFiltered && stats.best_day)) && (
          <Card className="bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-amber-500/20">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-500/20 rounded-full flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-amber-400">
                    {isFiltered ? "Best Day in Period!" : "Personal Best!"}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    You killed <span className="font-bold text-foreground">
                      {(isFiltered ? filteredStats.best_day?.kills : stats.best_day?.kills) || 0} bosses
                    </span> on{" "}
                    <span className="font-medium text-foreground">
                      {new Date((isFiltered ? filteredStats.best_day?.date : stats.best_day?.date) || new Date()).toLocaleDateString("en-US", { 
                        weekday: 'long', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </span>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Bosses with Images */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Sword className="w-5 h-5" />
                Top Hunted Bosses
                {isFiltered && <span className="text-xs font-normal text-muted-foreground">(filtered)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {topBosses.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  No kills recorded {isFiltered ? "for this period" : "yet"}
                </p>
              ) : (
                <div className="space-y-3">
                  {topBosses.map(([bossName, count], index) => (
                    <div 
                      key={bossName} 
                      className="flex items-center gap-3 p-2 rounded-lg bg-background/50"
                    >
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                        index === 0 ? "bg-yellow-500/20 text-yellow-400" :
                        index === 1 ? "bg-slate-400/20 text-slate-300" :
                        index === 2 ? "bg-amber-700/20 text-amber-600" :
                        "bg-muted text-muted-foreground"
                      }`}>
                        {index + 1}
                      </span>
                      {/* Boss Image */}
                      <div className="w-10 h-10 bg-background rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                        <img 
                          src={getBossImage(bossName)} 
                          alt={bossName}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <span className="flex-1 font-medium truncate">{bossName}</span>
                      <span className="text-primary font-bold">{count}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Kills with Images */}
          <Card className="bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent Kills
                {isFiltered && <span className="text-xs font-normal text-muted-foreground">(filtered)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[300px] pr-4">
                {recentKills.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    No kills recorded {isFiltered ? "for this period" : "yet"}
                  </p>
                ) : (
                  <div className="space-y-2">
                    {recentKills.map((kill) => (
                      <div 
                        key={kill.id} 
                        className="flex items-center gap-3 p-3 rounded-lg bg-background/50 border border-border/50"
                      >
                        {/* Boss Image */}
                        <div className="w-10 h-10 bg-card rounded flex-shrink-0 flex items-center justify-center overflow-hidden">
                          <img 
                            src={getBossImage(kill.boss_name)} 
                            alt={kill.boss_name}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{kill.boss_name}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>CH {kill.channel + 1}</span>
                            <span className="px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">
                              {kill.tracker_type}
                            </span>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(kill.killed_at)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Ko-fi Support */}
        <Card className="bg-[#FF5E5B]/5 border-[#FF5E5B]/20 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Heart className="w-6 h-6 text-[#FF5E5B] fill-[#FF5E5B]" />
                <div>
                  <h3 className="font-semibold text-[#FF5E5B]">Support the Project</h3>
                  <p className="text-xs text-muted-foreground">Help keep the servers running!</p>
                </div>
              </div>
              <Button 
                onClick={() => setShowKofiModal(true)}
                size="sm"
                className="bg-[#FF5E5B] hover:bg-[#FF5E5B]/90 text-white"
              >
                <Heart className="w-4 h-4 mr-1 fill-white" />
                Ko-fi
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Ko-fi Modal */}
      <KofiModal open={showKofiModal} onOpenChange={setShowKofiModal} />
    </div>
  );
};

export default StatsPage;
